import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DayPicker,
  getDefaultClassNames,
  type DateRange as PartialDateRange,
} from 'react-day-picker';
import 'react-day-picker/style.css';
import { isToday } from 'date-fns';
import { Button } from '@scholastic/mockingjay';
import { DateInput } from '@data/components/report/ReportFilterBar/dropdowns/DateRangeDropdown/DatePicker/DateInput/DateInput';
import ErrorText from '@data/components/basic/ErrorText/ErrorText';
import CircleExclamationSolidIcon from '@data/assets/circle-exclamation-solid.svg';
import { getSchoolYearDateRange } from '@data/utils/get-school-year';
import type { DateRange } from '@data/main';

export interface DayPickerProps {
  customDateRange: DateRange | null;
  setCustomDateRange: (dates: DateRange) => void;
  setShowCustomDatePicker: (value: boolean) => void;
}

export const DATE_RANGE_ERROR = 'End date must be after start date';
export const INVALID_DATE_ERROR = 'Invalid date';
export const FUTURE_DATE_ERROR = 'Cannot select future dates';
export const NOT_CURRENT_ERROR = 'Cannot select date before July 15';

export function DatePicker({
  customDateRange,
  setCustomDateRange,
  setShowCustomDatePicker,
}: DayPickerProps) {
  // The filter bar renders every filter twice below the laptop breakpoint, so
  // a fixed id would point both copies' labels at the first one in the page.
  const pickerId = useId();
  const defaultClassNames = getDefaultClassNames();
  const containerRef = useRef<HTMLDivElement>(null);

  const [dateRange, setDateRange] = useState<
    PartialDateRange | null | undefined
  >(customDateRange ?? null);
  // The calendar fills the box the reader last put the cursor in, and moves on
  // to the end date once it has filled the start.
  const [activeField, setActiveField] = useState<'from' | 'to'>('from');
  const [displayMonth, setDisplayMonth] = useState<Date>(
    customDateRange?.from ?? new Date()
  );

  const [startDateIsInvalid, setStartDateIsInvalid] = useState(false);
  const [startDateIsFuture, setStartDateIsFuture] = useState(false);
  const [startDateNotCurrent, setStartDateNotCurrent] = useState(false);
  const [endDateIsInvalid, setEndDateIsInvalid] = useState(false);
  const [endDateIsFuture, setEndDateIsFuture] = useState(false);
  const [endDateNotCurrent, setEndDateNotCurrent] = useState(false);
  const [hasRangeError, setHasRangeError] = useState(false);

  const errorMessage = useMemo(() => {
    if (startDateIsInvalid || endDateIsInvalid) return INVALID_DATE_ERROR;
    if (startDateIsFuture || endDateIsFuture) return FUTURE_DATE_ERROR;
    if (startDateNotCurrent || endDateNotCurrent) return NOT_CURRENT_ERROR;
    if (hasRangeError) return DATE_RANGE_ERROR;
    return null;
  }, [
    startDateIsInvalid,
    endDateIsInvalid,
    startDateIsFuture,
    endDateIsFuture,
    startDateNotCurrent,
    endDateNotCurrent,
    hasRangeError,
  ]);

  const schoolYearDateRange: DateRange = useMemo(() => {
    return getSchoolYearDateRange();
  }, []);

  const singleSelectedDate = useMemo(() => {
    if (dateRange?.from && !dateRange?.to) return dateRange.from;
    if (!dateRange?.from && dateRange?.to) return dateRange.to;
    return undefined;
  }, [dateRange]);

  useEffect(() => {
    if (!dateRange?.from || !dateRange?.to) {
      setHasRangeError(false);
      return;
    }
    if (dateRange.from > dateRange.to) {
      setHasRangeError(true);
      return;
    }
    setHasRangeError(false);
  }, [dateRange]);

  const isDoneDisabled =
    !dateRange?.from ||
    !dateRange?.to ||
    hasRangeError ||
    errorMessage !== null;

  const hideSelectedDates =
    hasRangeError ||
    startDateIsInvalid ||
    endDateIsInvalid ||
    startDateNotCurrent ||
    endDateNotCurrent;

  const handleStartDateChange = useCallback(
    (date: Date | undefined) => {
      setDateRange((prev) => ({
        from: date,
        to: prev?.to,
      }));
      if (
        date &&
        date >= schoolYearDateRange.from &&
        date <= schoolYearDateRange.to
      ) {
        setDisplayMonth(date);
      }
    },
    [schoolYearDateRange]
  );

  const handleEndDateChange = useCallback(
    (date: Date | undefined) => {
      setDateRange((prev) => ({
        from: prev?.from,
        to: date,
      }));
      if (
        date &&
        date >= schoolYearDateRange.from &&
        date <= schoolYearDateRange.to
      ) {
        setDisplayMonth(date);
      }
    },
    [schoolYearDateRange]
  );

  const handleDayPickerSelect = useCallback(
    (_range: PartialDateRange | undefined, day: Date) => {
      // clear date input errors when date is selected with calendar
      setStartDateIsInvalid(false);
      setStartDateIsFuture(false);
      setStartDateNotCurrent(false);
      setEndDateIsInvalid(false);
      setEndDateIsFuture(false);
      setEndDateNotCurrent(false);

      if (activeField === 'from') {
        setDateRange((prev) => ({ from: day, to: prev?.to }));
        setActiveField('to');
        return;
      }

      setDateRange((prev) => ({ from: prev?.from, to: day }));
    },
    [activeField]
  );

  const handleCancel = useCallback(() => {
    setShowCustomDatePicker(false);
  }, [setShowCustomDatePicker]);

  useEffect(() => {
    const handleClickOutside = (event: PointerEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowCustomDatePicker(false);
      }
    };

    // Pointer, not mouse: on touch the tap that picks "Custom range" is
    // followed by an emulated mousedown, which would close the picker it opened.
    document.addEventListener('pointerdown', handleClickOutside);
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
    };
  }, [setShowCustomDatePicker]);

  const handleDone = useCallback(() => {
    if (!dateRange?.from || !dateRange?.to || isDoneDisabled) return;

    setCustomDateRange({
      from: dateRange.from,
      to: dateRange.to,
    });
  }, [isDoneDisabled, dateRange, setCustomDateRange]);

  return (
    <div ref={containerRef} className="absolute left-0 top-full z-50 mt-1 flex">
      <div className="relative rounded-lg border bg-white shadow-lg">
        <div className="p-4 pb-6">
          <button
            type="button"
            onClick={handleCancel}
            className="absolute right-4 top-4 flex items-center justify-center rounded-full hover:bg-neutral-100"
            aria-label="Close custom date range picker"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <div className="flex justify-center pt-4">
            <DayPicker
              mode="range"
              disabled={[
                { before: schoolYearDateRange.from },
                { after: schoolYearDateRange.to },
              ]}
              classNames={{
                root: `${defaultClassNames.root}
                  [--rdp-range_start-color:white]
                  [--rdp-range_end-color:white]
                  [--rdp-range_start-date-background-color:var(--colors-brand-captain-blue)]
                  [--rdp-range_end-date-background-color:var(--colors-brand-captain-blue)]
                  [--rdp-selected-border:var(--colors-brand-captain-blue)]

                  max-laptop:[--rdp-day-width:38px]
                  max-laptop:[--rdp-day-height:38px]
                  max-laptop:[--rdp-day_button-width:36px]
                  max-laptop:[--rdp-day_button-height:36px]
                  max-laptop:[--rdp-nav_button-width:32px]
                  max-laptop:[--rdp-nav_button-height:32px]
                `,
                weekday: `${defaultClassNames.weekday} text-neutral-700`,
                range_middle: `${defaultClassNames.range_middle} bg-blue-50 font-bold`,
                chevron: `${defaultClassNames.chevron} !fill-black`,
              }}
              modifiers={{ singleSelected: singleSelectedDate }}
              modifiersClassNames={{
                today: !(
                  (dateRange?.from && isToday(dateRange.from)) ||
                  (dateRange?.to && isToday(dateRange.to))
                )
                  ? `${defaultClassNames.today} [&_button]:rounded-full [&_button]:border-2 [&_button]:border-brand-captainBlue [&_button]:!text-black`
                  : '',
                singleSelected:
                  '[&_button]:rounded-full [&_button]:bg-brand-captainBlue [&_button]:!text-white',
              }}
              components={{
                Chevron: ({ orientation }) => (
                  <svg aria-hidden="true" focusable="false" viewBox="0 0 32 32">
                    <path
                      d={
                        orientation === 'left'
                          ? 'M15 18l-6-6 6-6'
                          : 'M9 6l6 6-6 6'
                      }
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ),
              }}
              month={displayMonth}
              onMonthChange={setDisplayMonth}
              labels={{
                labelPrevious: () => 'Go to previous month',
                labelNext: () => 'Go to next month',
              }}
              selected={hideSelectedDates ? undefined : dateRange ?? undefined}
              onSelect={handleDayPickerSelect}
            />
          </div>
          <div className="flex px-0 laptop:px-3">
            <div className="flex-1">
              <DateInput
                label="Start Date"
                id={`start-date-input${pickerId}`}
                value={dateRange?.from}
                onChange={handleStartDateChange}
                hasError={
                  startDateIsInvalid ||
                  startDateNotCurrent ||
                  startDateIsFuture ||
                  hasRangeError
                }
                setDateIsFuture={setStartDateIsFuture}
                setDateIsInvalid={setStartDateIsInvalid}
                setDateNotCurrent={setStartDateNotCurrent}
                onFocus={() => setActiveField('from')}
              />
            </div>
            <div className="flex-1">
              <DateInput
                label="End Date"
                id={`end-date-input${pickerId}`}
                value={dateRange?.to}
                onChange={handleEndDateChange}
                hasError={
                  endDateIsInvalid ||
                  endDateNotCurrent ||
                  endDateIsFuture ||
                  hasRangeError
                }
                setDateIsFuture={setEndDateIsFuture}
                setDateIsInvalid={setEndDateIsInvalid}
                setDateNotCurrent={setEndDateNotCurrent}
                onFocus={() => setActiveField('to')}
              />
            </div>
          </div>
          {errorMessage && (
            <div className="mt-3 text-xs text-red-500">
              <ErrorText
                label={errorMessage}
                icon={CircleExclamationSolidIcon}
                iconClassName="h-4 w-4 mr-2"
              />
            </div>
          )}
        </div>

        <div className="h-[1px] bg-neutral-100" />

        <div className="flex justify-end gap-2 px-4 py-6">
          <Button
            variant="secondary"
            onClick={handleCancel}
            className="h-12 w-28"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDone}
            disabled={isDoneDisabled}
            className="h-12 w-28"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
